import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { ProductFeatureName } from 'dashboard/models/organization';
import { Role } from 'dashboard/models/role';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setCurrentPage, setFilters, setPageSize } from 'dashboard/redux/actions/roles';
import { RootState } from 'dashboard/redux/store';
import { loadAllRoles, loadRolesList } from 'dashboard/redux/thunk-actions/roles';
import DashboardRolesView from 'dashboard/views/DashboardRolesView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

type DashboardUserRolesRouteProps = PropsFromRedux;

const DashboardUserRolesRoute = (props: DashboardUserRolesRouteProps) => {
  const { currentPage, filters, pageSize, currentEnvironment, organization } = props;
  const productFeatureIsDisabled = productFeatureDisabled(
    organization,
    ProductFeatureName.RolesDashboard,
    currentEnvironment?.type,
  );

  const navigate = useNavigate();

  const { handlePageLinkClick, handlePageSizeClick, handleOmniSearch } = useTable({
    filters: filters,
    pageSize: pageSize,
    currentPage: currentPage,
    setFilters: props.setFilters,
    setCurrentPage: props.setCurrentPage,
    setPageSize: props.setPageSize,
  });

  const { loading: isLoading, callback: loadRoles } = useApiLoader({
    loader: () => props.loadRolesList({ currentPage, pageSize, filters }),
    deps: [currentPage, pageSize, filters],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load roles.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: createRoleError, callback: createRole } = useApiCallback(
    async ({ name, description }: { name: string; description: string }) => {
      const response = await SessionClientDashboard.postResourceCreate<Role>({
        attributes: {
          name,
          description,
        },
        type: ApiModel.Role,
      });

      loadRoles();
      props.loadAllRoles();

      props.showMessage(
        'Successfully created a new role, remember to enable environments',
        MessageType.Success,
      );

      navigate(`/dashboard/user-roles/${response.data.id}`);
    },
    [props.showMessage, navigate],
  );

  useApiErrorHandler({
    error: createRoleError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 403) {
        props.showMessage('Role with this name has been deactivated.', MessageType.Failure);
      } else if (error.status === 409) {
        props.showMessage('Role already exists with this name', MessageType.Failure);
      } else {
        props.showMessage('Error when creating your new role.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  const { error: destroyRoleError, callback: destroyRole } = useApiCallback(
    async (roleId: string) => {
      const response = await SessionClientDashboard.deleteResource<Role>(ApiEndpoint.Role, roleId);

      navigate('/dashboard/user-roles');
      props.showMessage(
        `Successfully deactivated the role ${response.data.attributes.name}`,
        MessageType.Success,
      );
    },
    [props.showMessage],
  );

  useApiErrorHandler({
    error: destroyRoleError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Error when deactivating the role.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Sorry, something went wrong. Please try again later or contact support.',
        MessageType.Failure,
      ),
  });

  return (
    <DashboardRolesView
      currentUser={props.currentUser}
      productFeatureIsDisabled={productFeatureIsDisabled}
      roles={props.roles}
      filters={filters}
      paginationParams={props.paginationParams}
      pageSize={pageSize}
      isLoading={isLoading}
      handleCreateRole={createRole}
      onPageLinkClick={handlePageLinkClick}
      onPageSizeClick={handlePageSizeClick}
      onOmniSearch={handleOmniSearch}
      environments={props.environments}
      destroyRole={async (roleId: string) => {
        await destroyRole(roleId);
        loadRoles();
      }}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    currentPage: state.roles.currentPage,
    currentUser: state.currentUser.currentUser,
    environments: state.organization.environments,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    filters: state.roles.filters,
    organization: state.organization.organization,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    pageSize: state.roles.pageSize,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    paginationParams: state.roles.paginationParams,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    roles: state.roles.roles,
  };
};

const mapDispatchToProps = {
  loadRolesList,
  loadAllRoles,
  setFilters,
  setPageSize,
  setCurrentPage,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardUserRolesRoute);
